import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ClipboardList, FileText, Mail, PenLine, Building2 } from "lucide-react";
import useSeo from "../hooks/useSeo";

const API = process.env.REACT_APP_BACKEND_URL;

const REGIONS = [
  { value: "Sjælland", label: "Sjælland (åbent)", active: true },
  { value: "Fyn", label: "Fyn (future expansion — vi modtager forespørgsler)", active: false },
  { value: "Jylland", label: "Jylland (future expansion — vi modtager forespørgsler)", active: false },
  { value: "Bornholm", label: "Bornholm (future expansion)", active: false },
  { value: "Andet", label: "Andet område", active: false },
];

const SERVICE_NEEDS = [
  { value: "same_day", label: "Same Day" },
  { value: "next_day", label: "Next Day" },
  { value: "evening", label: "Aftenlevering" },
  { value: "mixed", label: "Mix af flere" },
];

const PICKUP_FREQ = [
  { value: "daily", label: "Dagligt (mandag-fredag)" },
  { value: "3x_week", label: "3x ugentligt" },
  { value: "2x_week", label: "2x ugentligt" },
  { value: "weekly", label: "Ugentligt" },
  { value: "on_demand", label: "Efter behov" },
];

const PICKUP_TIMES = [
  { value: "morning_08_10", label: "Morgen (08-10)" },
  { value: "midday_10_13", label: "Formiddag (10-13)" },
  { value: "afternoon_13_16", label: "Eftermiddag (13-16)" },
  { value: "fleksibel", label: "Fleksibel" },
];

const initialForm = {
  company_name: "", cvr: "", contact_name: "", contact_email: "", contact_phone: "",
  pickup_address: "", pickup_region: "Sjælland",
  expected_monthly_packages: "", expected_packages_per_pickup_day: "",
  pickup_frequency: "daily", preferred_pickup_time: "morning_08_10",
  delivery_area: "", service_need: "next_day",
  average_package_weight_kg: "", max_package_weight_kg: "",
  package_size_notes: "", needs_returns: false,
  billing_email: "", comments: "",
};

export default function QuoteRequestPage() {
  useSeo({
    title: "Få tilbud — Erhvervslevering på Sjælland | EMA Kurer",
    description: "Bliv erhvervskunde hos EMA Kurer. Vi tilbyder afhentning, sortering og levering for virksomheder på Sjælland. Få et skræddersyet tilbud baseret på jeres pakkevolumen.",
    canonical: "https://emakurer.dk/erhverv/faa-tilbud",
  });
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        expected_monthly_packages: parseInt(form.expected_monthly_packages, 10) || 0,
        expected_packages_per_pickup_day: parseInt(form.expected_packages_per_pickup_day, 10) || 0,
        average_package_weight_kg: parseFloat(form.average_package_weight_kg) || 0,
        max_package_weight_kg: parseFloat(form.max_package_weight_kg) || 0,
      };
      const { data } = await axios.post(`${API}/api/quote-requests`, payload);
      // Scroll to top instantly so user sees success card immediately on re-render
      window.scrollTo(0, 0);
      setResult(data);
    } catch (err) {
      // Normalize FastAPI error detail: can be string, array (422 validation), or object.
      // Rendering an array/object as React child crashes the page → blank screen.
      const d = err?.response?.data?.detail;
      let msg = "Der opstod en fejl. Prøv igen eller skriv til erhverv@emakurer.dk.";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d)) msg = d.map((x) => x?.msg || JSON.stringify(x)).filter(Boolean).join(" · ");
      else if (d && typeof d === "object") msg = d.msg || JSON.stringify(d);
      setError(msg);
      window.scrollTo(0, 0);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-16">
        <div className="max-w-2xl w-full bg-white border-2 border-brand-red p-8 sm:p-10 shadow-lg" data-testid="quote-success-block">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-brand-red text-white flex items-center justify-center text-2xl font-bold shrink-0">✓</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-brand-dark">Tak for din forespørgsel</h1>
          </div>
          <p className="text-lg text-brand-text2 mb-6 leading-relaxed">{result.message}</p>
          <div className="bg-stone-50 p-5 border border-stone-200 text-sm space-y-2">
            <div><span className="text-brand-text2">Reference:</span> <strong className="text-brand-dark">{result.reference}</strong></div>
            <div><span className="text-brand-text2">Status:</span> <strong className="text-brand-dark">{result.status}</strong></div>
            {!result.is_active_area && (
              <div className="mt-3 pt-3 border-t border-stone-200 text-amber-800 text-sm leading-relaxed">
                Vi har modtaget din forespørgsel som <strong>future expansion lead</strong> og kontakter jer, når vi kan tilbyde levering i jeres område.
              </div>
            )}
          </div>
          <div className="mt-6 text-sm text-brand-text2 leading-relaxed">
            En bekræftelse er sendt til din email. Vi vender tilbage inden for én arbejdsdag med et skræddersyet tilbud.
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/" className="px-5 py-3 bg-brand-red text-white font-semibold" data-testid="quote-success-home">Tilbage til forside</Link>
            <Link to="/erhverv" className="px-5 py-3 border-2 border-brand-dark text-brand-dark font-semibold" data-testid="quote-success-erhverv">Læs mere om EMA Erhverv</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="kicker mb-3" data-testid="quote-kicker">Erhvervslevering på Sjælland</div>
        <h1 className="text-4xl sm:text-5xl font-bold text-brand-dark mb-4" data-testid="quote-h1">Få tilbud — bliv erhvervskunde</h1>
        <p className="text-lg text-brand-text2 mb-4 leading-relaxed">
          EMA Kurer tilbyder afhentning, sortering og levering for virksomheder på Sjælland.
        </p>
        <p className="text-brand-text2 mb-4 leading-relaxed">
          Vi har i øjeblikket åbent for erhvervslevering på Sjælland. Vi arbejder løbende på at udvide til Fyn og Jylland.
          Er din virksomhed udenfor Sjælland, kan du stadig sende en forespørgsel, så kontakter vi dig, når vi kan tilbyde levering i dit område.
        </p>
        <p className="text-brand-text2 mb-10 leading-relaxed">
          Udfyld formularen, så kontakter vi jer med et tilbud baseret på jeres pakkevolumen, afhentningsadresse og leveringsbehov. Vi vender tilbage inden for én arbejdsdag.
        </p>

        {/* Process step diagram — set expectations before form */}
        <section className="mb-12 bg-stone-50 border border-stone-200 p-6 sm:p-8" data-testid="quote-process-diagram">
          <h2 className="font-display font-bold text-2xl text-brand-dark mb-6">Hvad sker der, når du har sendt forespørgslen?</h2>
          <ol className="space-y-5">
            {[
              { i: ClipboardList, n: "1", t: "Vi gennemgår jeres behov", d: "Vi kigger på område, volumen, afhentningsadresse og leveringsbehov." },
              { i: FileText,      n: "2", t: "Vi laver et skræddersyet tilbud", d: "Tilbuddet baseres på jeres forventede pakkevolumen og driftsflow." },
              { i: Mail,          n: "3", t: "I modtager tilbud og aftalelink", d: "Vi sender tilbuddet på mail sammen med link til digital erhvervsaftale." },
              { i: PenLine,       n: "4", t: "I signerer digitalt", d: "Når aftalen er gennemgået og accepteret, signerer I digitalt." },
              { i: Building2,     n: "5", t: "Vi opretter jeres erhvervskonto", d: "Når betalingsmetode, fakturaoplysninger og afhentningsadresse er godkendt, kan booking åbnes." },
            ].map((s) => (
              <li key={s.n} className="flex items-start gap-4" data-testid={`quote-step-${s.n}`}>
                <div className="shrink-0 w-10 h-10 bg-brand-red text-white flex items-center justify-center font-bold text-lg" aria-hidden="true">
                  {s.n}
                </div>
                <div className="flex-1 pt-1">
                  <div className="flex items-center gap-2 mb-1">
                    <s.i size={16} className="text-brand-red shrink-0" strokeWidth={2.2} />
                    <h3 className="font-display font-semibold text-base text-brand-dark">{s.t}</h3>
                  </div>
                  <p className="text-sm text-brand-text2 leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {error && (
          <div className="mb-6 p-4 border-2 border-red-500 bg-red-50 text-red-800" data-testid="quote-error">{error}</div>
        )}

        <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
          <section>
            <h2 className="text-lg font-bold text-brand-dark mb-3 uppercase tracking-wide">Virksomhed</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Firmanavn *" v={form.company_name} on={set("company_name")} t="q-company" req />
              <Field label="CVR-nummer (8 cifre) *" v={form.cvr} on={set("cvr")} t="q-cvr" req maxLength={10} />
              <Field label="Kontaktperson *" v={form.contact_name} on={set("contact_name")} t="q-contact" req />
              <Field label="Email *" type="email" v={form.contact_email} on={set("contact_email")} t="q-email" req />
              <Field label="Telefon *" type="tel" v={form.contact_phone} on={set("contact_phone")} t="q-phone" req />
              <Field label="Faktura-email *" type="email" v={form.billing_email} on={set("billing_email")} t="q-billing-email" req />
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-brand-dark mb-3 uppercase tracking-wide">Afhentning</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Afhentningsadresse / lager *" v={form.pickup_address} on={set("pickup_address")} t="q-pickup-address" req />
              <Select label="Afhentningsregion *" v={form.pickup_region} on={set("pickup_region")} t="q-pickup-region" options={REGIONS.map(r => ({ value: r.value, label: r.label }))} />
              <Select label="Afhentningsfrekvens *" v={form.pickup_frequency} on={set("pickup_frequency")} t="q-pickup-freq" options={PICKUP_FREQ} />
              <Select label="Foretrukken afhentningstid *" v={form.preferred_pickup_time} on={set("preferred_pickup_time")} t="q-pickup-time" options={PICKUP_TIMES} />
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-brand-dark mb-3 uppercase tracking-wide">Volumen og service</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Forventet månedlig pakkevolumen *" type="number" v={form.expected_monthly_packages} on={set("expected_monthly_packages")} t="q-monthly-vol" req min="1" />
              <Field label="Pakker pr. afhentningsdag *" type="number" v={form.expected_packages_per_pickup_day} on={set("expected_packages_per_pickup_day")} t="q-pickup-day-vol" req min="1" />
              <Select label="Serviceniveau *" v={form.service_need} on={set("service_need")} t="q-service" options={SERVICE_NEEDS} />
              <Field label="Leveringsområde / postnumre *" v={form.delivery_area} on={set("delivery_area")} t="q-delivery-area" req placeholder="fx 1000-2999 København + omegn" />
              <Field label="Gennemsnitlig pakkevægt (kg) *" type="number" step="0.1" v={form.average_package_weight_kg} on={set("average_package_weight_kg")} t="q-avg-weight" req />
              <Field label="Maks. pakkevægt (kg) *" type="number" step="0.1" v={form.max_package_weight_kg} on={set("max_package_weight_kg")} t="q-max-weight" req />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-semibold text-brand-dark mb-1">Pakke-størrelser (noter)</label>
              <textarea value={form.package_size_notes} onChange={set("package_size_notes")} rows={2}
                className="w-full p-3 border-2 border-stone-300 text-sm"
                placeholder="fx max LxBxH 60x40x40 cm, fragile gods, paller, mv."
                data-testid="q-size-notes" />
            </div>
            <div className="mt-4 flex items-center gap-2">
              <input type="checkbox" id="q-returns" checked={form.needs_returns} onChange={set("needs_returns")} data-testid="q-returns" />
              <label htmlFor="q-returns" className="text-sm text-brand-dark">Vi har behov for retur-håndtering</label>
            </div>
          </section>

          <section>
            <label className="block text-sm font-semibold text-brand-dark mb-1">Kommentarer / særlige behov</label>
            <textarea value={form.comments} onChange={set("comments")} rows={4}
              className="w-full p-3 border-2 border-stone-300 text-sm"
              placeholder="Hvis der er andet vi skal vide for at lave det rigtige tilbud..."
              data-testid="q-comments" />
          </section>

          <div className="border-t pt-6 flex flex-col sm:flex-row gap-3 items-start">
            <button type="submit" disabled={submitting}
              className="px-8 py-4 bg-brand-red text-white font-semibold uppercase tracking-wide disabled:opacity-50"
              data-testid="quote-submit-btn">
              {submitting ? "Sender..." : "Send forespørgsel"}
            </button>
            <p className="text-xs text-brand-text2 max-w-md">
              Vi behandler dine oplysninger i overensstemmelse med vores <Link to="/privatlivspolitik" className="underline">privatlivspolitik</Link>.
              Vi kontakter dig hurtigst muligt med et tilbud baseret på jeres oplysninger.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, v, on, t, type = "text", req, ...rest }) => (
  <div>
    <label className="block text-sm font-semibold text-brand-dark mb-1">{label}</label>
    <input type={type} value={v} onChange={on} required={req}
      className="w-full p-3 border-2 border-stone-300 text-sm focus:outline-none focus:border-brand-red"
      data-testid={t} {...rest} />
  </div>
);

const Select = ({ label, v, on, t, options }) => (
  <div>
    <label className="block text-sm font-semibold text-brand-dark mb-1">{label}</label>
    <select value={v} onChange={on}
      className="w-full p-3 border-2 border-stone-300 text-sm bg-white focus:outline-none focus:border-brand-red"
      data-testid={t}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
